import React from "react";
import { Box, Select, MenuItem, IconButton, SelectChangeEvent } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import colors from "../../themes/colors";
import { dt } from "../../themes/redesignTokens";

interface LightTablePaginationProps {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  rowsPerPageLabel?: string;
  ofLabel?: string;
}

const LightTablePagination = ({
  totalCount,
  currentPage,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  rowsPerPageLabel = "Rows per page:",
  ofLabel = "of"
}: LightTablePaginationProps) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const from = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange(Number(event.target.value));
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <React.Fragment key={i}>
          <Box
            component="span"
            onClick={() => handlePageClick(i)}
            className="text-14-regular"
            sx={{
              cursor: i === currentPage ? "default" : "pointer",
              color: i === currentPage ? colors.primary.violetDark : colors.secondary.gray40,
              borderBottom: i === currentPage ? `2px solid ${dt.blue}` : "none",
              paddingBottom: "2px",
              px: "4px",
              "&:hover": {
                color: i === currentPage ? colors.primary.violetDark : dt.blue
              }
            }}
          >
            {i}
          </Box>
        </React.Fragment>
      );
    }
    return pages;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "24px",
        padding: "0 16px",
        borderTop: `1px solid ${colors.secondary.gray80}`,
        backgroundColor: colors.primary.white1,
        borderRadius: "0 0 8px 8px"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box
          className="text-14-regular"
          color={colors.secondary.gray40}
        >
          {rowsPerPageLabel}:
        </Box>
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          size="small"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "& .MuiSelect-select": {
              padding: "4px 26px 4px 10px",
            },
            "& .MuiSelect-icon": {
              color: colors.primary.violetDark
            }
          }}
        >
          {pageSizeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              <Box className="text-14-regular" color={colors.primary.violetDark}>{option}</Box>
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Box
          className="text-14-regular"
          color={colors.secondary.gray40}
        >
          {from}-{to} {ofLabel} {totalCount}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <IconButton
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            size="small"
            sx={{
              padding: "4px",
              color: colors.secondary.gray40,
              "&:hover": {
                backgroundColor: colors.secondary.gray90
              },
              "&.Mui-disabled": {
                color: colors.secondary.gray80
              }
            }}
          >
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {renderPageNumbers()}
          </Box>

          <IconButton
            onClick={handleNextPage}
            disabled={currentPage >= totalPages || totalCount === 0}
            size="small"
            sx={{
              padding: "4px",
              color: colors.secondary.gray40,
              "&:hover": {
                backgroundColor: colors.secondary.gray90
              },
              "&.Mui-disabled": {
                color: colors.secondary.gray80
              }
            }}
          >
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default LightTablePagination;
